import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  forwardRef,
  TemplateRef,
  Input,
  Inject,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer } from '@angular/platform-browser';
import { IIremboFileUpload } from '../../models/irembo-file-upload-response.model';
import { FileUploadSource } from '../../models/file-upload-source.enum';
import { Subject, Subscription, catchError, finalize, of } from 'rxjs';
import { IPreviousAttachmentEvent } from '../../models/previous-attachment-event';
import { DOCUMENT } from '@angular/common';
import { ICertificateAttachmentEvent } from '../../models/certificate-attachment-event';
import errorMessages from '../../models/photo-validation-error-messages';
import { FormStateService } from '../../services/formly/form-state.service';
import { VirusScanStatus } from '../../../models/virus-scan.model';
import { VirusScanService } from '../../../services/virus-scan/virus-scan.service';
import { FileUploadValidation } from '../../models/irembo-file-upload-validations.model';
import { EValidationType } from '../../models/attachment-validation-types.enum';
import * as XLSX from 'xlsx';
import { IEnvironment } from '../../models/environment.model';

@Component({
  selector: 'irembogov-file-upload',
  templateUrl: './irembo-file-upload.component.html',
  styleUrls: ['./irembo-file-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IremboFileUploadComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: IremboFileUploadComponent,
      multi: true,
    },
  ],
})
export class IremboFileUploadComponent
  implements ControlValueAccessor, Validator, OnInit, OnChanges, OnDestroy
{
  @Input() allowedFormats: string[] = ['png', 'jpg', 'pdf'];
  @Input() maximumUploadSize = 1024;
  @Input() minimumUploadSize = 100;
  @Input() id: unknown;
  @Input() previousAttachments: IPreviousAttachmentEvent[] = [];
  @Input() certificateAttachments: ICertificateAttachmentEvent[] = [];
  @Input() placeholder = 'Select file to upload';
  @Input() filePath: string | undefined;
  @Input() disableVirusScan = false;
  @Output() fileUploadEvent = new EventEmitter<IIremboFileUpload>();
  @Output() previewFileEvent = new EventEmitter<unknown>();
  @Output() selectFileEvent = new EventEmitter<unknown>();
  @Output() selectPreviousAttachmentEvent =
    new EventEmitter<IPreviousAttachmentEvent>();
  @Output() selectCertificateAttachmentEvent =
    new EventEmitter<ICertificateAttachmentEvent>();
  @Output() previousAttachmentEventScroll = new EventEmitter<
    Record<string, number>
  >();
  @Output() certificateAttachmentEventScroll = new EventEmitter<
    Record<string, number>
  >();
  @Output() handleValidationErrors = new EventEmitter<ValidationErrors>();
  @Input() progress: Subject<number> = new Subject<number>();
  @Input() loadingFile = false;
  @Input() totalPreviousAttachments = 0;
  @Input() totalCertificateAttachments = 0;

  @Input() photoValidationResponse: Record<string, unknown> | undefined;
  @Input() validatePhoto = false;
  @Input() validatingPhoto = false;
  @Input() attachmentCode: string | undefined;
  @Input() duplicateFile = false;
  @Input() isCertificate: string | undefined;
  @Input() validations: FileUploadValidation[] = [];
  @Output() duplicateFileChange = new EventEmitter<boolean>();

  // Virus scanning related properties
  scanForVirus = false;
  scanningForVirus = false;
  virusScanStatus: VirusScanStatus = VirusScanStatus.NOT_SCANNED;
  virusScanMessage = '';

  loadingFileName = '';

  disabled = false;
  file: IIremboFileUpload | null | undefined;
  hasErrors = false;
  addDragClass = false;
  base64Data: string | undefined;
  failedChecks: string[] = [];
  invalidPhoto = false;
  photoValiationErrorMessages: Record<string, string> = errorMessages;
  fileAttachmentNotValid = false;
  validationsMessage = '';
  validationsIsssuesDetails: { value: any; message: string }[] = [];
  isValidatingAttachment = false;
  isValidationRequiredColumn = false;
  requiredColumnMessage = '';
  completedValidationsCount = 0;
  totalValidations = 0;
  analysisCompleted = false;
  isInvalidAttachment = false;
  invalidAttachmentMessage = 'Invalid attachment, please add valid file';

  constructor(
    public modalService: NgbModal,
    private sanitizer: DomSanitizer,
    private cd: ChangeDetectorRef,
    private formStateService: FormStateService,
    @Inject(DOCUMENT) private _document: Document,
    private virusScanService: VirusScanService,
    @Inject('environment') private environment: IEnvironment
  ) {
    // Use the environment setting to determine if virus scanning is enabled
    this.scanForVirus =
      (this.environment.isDocumentScanningEnabled ?? true) !== false &&
      !this.disableVirusScan;
  }

  /* eslint-disable @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function*/
  private _onChange = (value: unknown) => {};
  private _onTouch = (value: unknown) => {};
  private _onValidationChange = () => {};
  /* eslint-enable */

  private subscriptions = new Subscription();

  writeValue(obj: IIremboFileUpload): void {
    this.file = obj;
    if (obj) {
      const fileSizeInKB = this.file.fileSize / 1024;
      this._onValidationChange();
      this.validateUpload(fileSizeInKB);
      this.base64Data = obj.content as string;
    }
  }

  ngOnInit(): void {
    // Initialize virus scanning setting based on current inputs
    this.scanForVirus =
      (this.environment.isDocumentScanningEnabled ?? true) !== false &&
      !this.disableVirusScan;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['validatingPhoto']) {
      this._onValidationChange();
    }

    // Handle disableVirusScan changes
    if (changes?.['disableVirusScan']) {
      this.scanForVirus =
        (this.environment.isDocumentScanningEnabled ?? true) !== false &&
        !this.disableVirusScan;
    }

    if (this.validatePhoto) {
      if (
        changes?.['photoValidationResponse'] &&
        changes['photoValidationResponse'].currentValue
      ) {
        this.failedChecks = [];
        const isImage = this.file?.fileType?.includes('image');
        if (isImage) {
          const checks = changes['photoValidationResponse'].currentValue;
          this.failedChecks = this.checkPhotoResponse(checks);
          this.hasErrors = this.failedChecks.length > 0;
          this.invalidPhoto = this.hasErrors;
        }
      }
      this._onValidationChange();
    }
    if (this.duplicateFile) {
      this.hasErrors = true;
      this._onChange(this.file);
      this._onTouch(this.file);
    }
  }

  registerOnChange(fn: (_: unknown) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: (_: unknown) => void): void {
    this._onTouch = fn;
  }

  registerOnValidatorChange?(fn: () => void): void {
    this._onValidationChange = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  async removeFile() {
    if (this.file && !this.duplicateFile) {
      // Compute SHA-256 hash of file content
      const fileHash = await this.formStateService.computeSHA256(
        this.file.content as string
      );
      this.formStateService.removeHashAttachmentFromArray(
        this.file?.fileName,
        fileHash
      );
    }
    this.file = null;
    this.loadingFileName = '';
    this.addDragClass = false;
    this._onChange(null);
    this._onTouch(null);
    this.failedChecks = [];
    this.invalidPhoto = false;
    this.hasErrors = false;
    this._onValidationChange();
    this.duplicateFile = false;
    this.duplicateFileChange.emit(false);
    this.fileAttachmentNotValid = false;
    this.validationsIsssuesDetails = [];
    this.isValidationRequiredColumn = false;
    this.isInvalidAttachment = false;
    this.cd.detectChanges();
  }

  onClickFileUpload(e: Event) {
    e.preventDefault();
    e.stopPropagation();
  }

  onPreviousAttachmentScroll() {
    const elements = this._document.getElementsByClassName(
      'previousAttachmentsContainer'
    );
    if (elements.length > 0) {
      const scrollPosition = Math.round(elements[0].scrollTop);
      const scrollHeight = elements[0].scrollHeight;
      const clientHeight = elements[0].clientHeight;
      this.previousAttachmentEventScroll.emit({
        scrollPosition,
        scrollHeight,
        clientHeight,
      });
    }
  }

  onCertificateAttachmentScroll() {
    const elements = this._document.getElementsByClassName(
      'certificateAttachmentsContainer'
    );
    if (elements.length > 0) {
      const scrollPosition = Math.round(elements[0].scrollTop);
      const scrollHeight = elements[0].scrollHeight;
      const clientHeight = elements[0].clientHeight;
      this.certificateAttachmentEventScroll.emit({
        scrollPosition,
        scrollHeight,
        clientHeight,
      });
    }
  }

  change(event: unknown) {
    this.hasErrors = false;
    this.failedChecks = [];
    this.invalidPhoto = false;
    if (event instanceof Event) {
      const fileList = this.getFileListFromEvent(event);
      if (fileList) {
        this.processFile(fileList[0]);
      }
    }
    this._onValidationChange();
  }

  private getFileListFromEvent(event: Event): FileList | null {
    return (event.target as HTMLInputElement).files;
  }

  private processFile(file: File) {
    this.file = {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      content: null,
      fileDownloadName: '',
      source: FileUploadSource.DEVICE,
      attachmentCode: this.attachmentCode,
      isCertificate: this.isCertificate,
    };

    const fileSizeInKB = file.size / 1024;
    this.validateUpload(fileSizeInKB);

    if (!this.hasErrors) {
      // Perform virus scan if enabled
      if (this.scanForVirus) {
        this.scanFileForVirus(file);
      } else {
        this.loadFile(file);
      }
    }
  }

  private loadFile(file: File) {
    const reader = new FileReader();
    reader.onprogress = (event: ProgressEvent<FileReader>) => {
      this.handleFileProgress(event, file);
    };

    reader.onload = () => {
      this.handleFileLoaded(reader.result as string);
    };

    reader.readAsDataURL(file);
  }

  private handleFileProgress(event: ProgressEvent<FileReader>, file: File) {
    this.loadingFile = true;
    this.loadingFileName = file.name;
    if (event.lengthComputable) {
      const progressPercentage = Math.round((event.loaded / event.total) * 100);
      this.progress.next(progressPercentage);
    }
  }

  private async handleFileLoaded(result: string) {
    this.loadingFile = false;
    this.filePath = this.sanitizer.bypassSecurityTrustResourceUrl(
      result
    ) as string;
    this.base64Data = result;
    if (this.file) {
      this.file.content = result;
      this._onChange(this.file);
      this._onTouch(this.file);
      this.fileUploadEvent.emit(this.file);
      this.modalService.dismissAll();
    }
    // Compute SHA-256 hash of file content
    const fileHash = await this.formStateService.computeSHA256(result);
    // Check if file content is already in uploadedFiles
    if (this.formStateService.isDuplicateFile(fileHash)) {
      this.duplicateFile = true;
      this.duplicateFileChange.emit(true);
      this.hasErrors = true;
      this._onChange(this.file);
      this._onTouch(this.file);
      if (this.file) {
        this.fileUploadEvent.emit(this.file);
      }
      this.cd.detectChanges();
      return;
    }
    if (this.validations && this.validations.length > 0) {
      this.validateUploadedAttachment(result);
    }
    if (this.file) {
      this.formStateService.saveHashAttachmentInArray(
        this.file.fileName,
        fileHash
      );
    }
  }

  private validateUpload(fileSizeInKB: number) {
    const ext = this.file?.fileName.split('.').pop();
    if (
      fileSizeInKB > this.maximumUploadSize ||
      fileSizeInKB < this.minimumUploadSize ||
      (this.allowedFormats?.length > 1 &&
        ext &&
        !this.checkIfExtensionIsAllowed(ext)) ||
      this.duplicateFile
    ) {
      this.hasErrors = true;
      this._onChange(this.file);
      this._onTouch(this.file);
      if (this.file) {
        this.fileUploadEvent.emit(this.file);
      }
      this.modalService.dismissAll();
      this.cd.detectChanges();
    }
  }

  getPhotoValidationErrorMessage(key: string) {
    return this.photoValiationErrorMessages[key] as string;
  }

  private checkIfExtensionIsAllowed(ext: string) {
    return (
      this.allowedFormats &&
      this.allowedFormats
        .map((format: string) => format.toLowerCase())
        .indexOf(ext.toLowerCase()) > -1
    );
  }

  preview(content: TemplateRef<unknown>) {
    this.modalService.open(content, {
      ariaLabelledBy: 'preview-document',
      size: 'xl',
    });
    this.previewFileEvent.emit(this.file);
  }

  selectFile(content: TemplateRef<unknown>) {
    this.selectFileEvent.emit('select-file');
    this.modalService.open(content, {
      ariaLabelledBy: 'preview-document',
      size: 'xl',
    });
  }

  get canPreview() {
    let isImageOrPdf = false;
    const fileType = this.file?.fileType;
    if (fileType) {
      isImageOrPdf = fileType.includes('image') || fileType.includes('pdf');
    }
    return isImageOrPdf;
  }

  acceptList() {
    return this.allowedFormats
      ? this.allowedFormats.map(ele => `.${ele.toLowerCase()}`).toString()
      : '';
  }

  validate(): ValidationErrors | null {
    if (!this.file) return null;
    const validationErrors: ValidationErrors = {};
    const ext = this.file?.fileName.split('.').pop();

    if (!ext) {
      validationErrors['invalidfileformat'] = true;
    }

    if (
      this.allowedFormats?.length > 1 &&
      ext &&
      !this.checkIfExtensionIsAllowed(ext)
    ) {
      validationErrors['invalidfileformat'] = true;
    }

    const fileSizeInKB = this.file.fileSize / 1024;
    if (fileSizeInKB < this.minimumUploadSize) {
      validationErrors['minimumUploadSize'] = true;
    }

    if (fileSizeInKB > this.maximumUploadSize) {
      validationErrors['maximumUploadSize'] = true;
    }

    if (this.invalidPhoto && this.validatePhoto) {
      validationErrors['invalidPhoto'] = true;
    }

    if (this.validatingPhoto) {
      validationErrors['validatingPhoto'] = true;
    }

    if (this.duplicateFile) {
      validationErrors['duplicateAttachment'] = true;
    }

    if (this.fileAttachmentNotValid) {
      validationErrors['notUniqueDataInAttachment'] = true;
    }

    // Add virus scan validation
    if (this.scanForVirus) {
      if (this.scanningForVirus) {
        validationErrors['scanningForVirus'] = true;
      }

      if (this.virusScanStatus === VirusScanStatus.INFECTED) {
        validationErrors['virusDetected'] = true;
      }

      if (this.virusScanStatus === VirusScanStatus.ERROR) {
        validationErrors['virusScanError'] = true;
      }
    }

    return Object.keys(validationErrors).length ? validationErrors : null;
  }

  getIcon(fileName: unknown): string {
    const ext = (fileName as string).split('.').pop();
    return ext === 'pdf' ? 'fa-regular fa-file-pdf' : 'fa-regular fa-image';
  }

  handleSelectPreviousAttachment(item: IPreviousAttachmentEvent) {
    this.loadingFileName = item.fileName;
    this.selectPreviousAttachmentEvent.emit(item);
  }

  handleSelectCertificateAttachment(item: ICertificateAttachmentEvent) {
    this.loadingFileName = item.certificateName;
    this.selectCertificateAttachmentEvent.emit(item);
  }

  castToString(value: unknown) {
    return value as string;
  }

  toogledragClass(event: Event, status: boolean) {
    this.addDragClass = status;
  }

  checkPhotoResponse(response: Record<string, unknown>) {
    const failedChecks: string[] = [];
    if (response) {
      for (const prop in response) {
        if (
          Object.hasOwnProperty.call(response, prop) &&
          response[prop] === false
        ) {
          failedChecks.push(prop);
        }
      }
    }
    return failedChecks;
  }

  private isDuplicateFile(fileHash: string): boolean {
    const uploadedFiles = this.formStateService.getUploadedFiles();
    return uploadedFiles.some(file => file.hash === fileHash);
  }

  private async computeSHA256(fileContent: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(fileContent); // Convert Base64 to bytes
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Scans a file for viruses before loading
   * @param file The file to scan
   */
  private scanFileForVirus(file: File): void {
    this.scanningForVirus = true;
    this.virusScanStatus = VirusScanStatus.SCANNING;
    this.loadingFile = true;
    this.virusScanMessage = 'Scanning file for viruses...';
    this.loadingFileName = file.name;
    this.progress.next(0);

    // Show progress while scanning
    let scanProgress = 0;
    const progressInterval = setInterval(() => {
      if (scanProgress < 90) {
        // Cap at 90% until we get final result
        scanProgress += 5;
        this.progress.next(scanProgress);
      }
    }, 200);

    this.subscriptions.add(
      this.virusScanService
        .scanFile(file)
        .pipe(
          finalize(() => {
            clearInterval(progressInterval);
            this.scanningForVirus = false;
            this.progress.next(100); // Complete the progress
            this.cd.detectChanges();
          }),
          catchError(error => {
            this.virusScanStatus = VirusScanStatus.ERROR;

            this.hasErrors = true;
            this.loadingFile = false;

            // Remove the infected file from the component state
            this.file = null;
            this._onChange(null);
            this._onTouch(null);

            this._onValidationChange();

            // Emit event so parent components can show appropriate feedback
            this.handleValidationErrors.emit({
              virusDetected: false,
              virusScanError: this.virusScanStatus === VirusScanStatus.ERROR,
              message:
                'Error scanning file: ' + (error.message || 'Unknown error'),
            });

            this.cd.detectChanges();
            return of(null);
          })
        )
        .subscribe(response => {
          if (response) {
            if (response.status && response.data && response.data.clean) {
              this.virusScanStatus = VirusScanStatus.CLEAN;
              this.virusScanMessage = 'No virus detected';
              // Load the file as it's clean
              this.loadFile(file);
            } else {
              this.virusScanStatus = VirusScanStatus.INFECTED;
              this.virusScanMessage =
                'Virus detected: ' +
                (response.data.virusThreats || 'File cannot be uploaded');
              this.hasErrors = true;
              this.loadingFile = false;

              // Remove the infected file from the component state
              this.file = null;
              this._onChange(null);
              this._onTouch(null);

              this._onValidationChange();
              // Emit event so parent components can show appropriate feedback
              this.handleValidationErrors.emit({
                virusDetected: true,
                message: this.virusScanMessage,
              });
              this.cd.detectChanges();
            }
          }
        })
    );
  }

  private validateUploadedAttachment(result: string) {
    this.completedValidationsCount = 0;
    this.totalValidations = this.validations.length;
    this.analysisCompleted = false;
    this.isValidatingAttachment = true;

    for (let i = 0; i < this.validations.length; i++) {
      const validation: FileUploadValidation = this.validations[i];
      this.swicthBetweenAttachmentValidators(validation, result);
      this.completedValidationsCount++;
    }
    if (this.completedValidationsCount === this.totalValidations) {
      this.analysisCompleted = true;
      this.isValidatingAttachment = false;
      this.cd.detectChanges();
    }
  }

  swicthBetweenAttachmentValidators(
    validation: FileUploadValidation,
    result: string
  ) {
    const resultAsArrayBuffer = this.base64ToArrayBuffer(result);
    const data: Uint8Array = new Uint8Array(resultAsArrayBuffer);
    const workbook: XLSX.WorkBook = XLSX.read(data, { type: 'array' });

    const sheetName: string = workbook.SheetNames[0];
    const worksheet: XLSX.WorkSheet = workbook.Sheets[sheetName];
    const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
    switch (validation.type) {
      case EValidationType.UNIQUE: {
        this.checkColumnUniqueness(
          jsonData,
          validation.column,
          validation.message,
          validation.startIndex
        );
        break;
      }
      case EValidationType.LENGTH: {
        const isLengthOk = this.checkColumnLength(
          jsonData,
          validation.column,
          validation.minLength,
          validation.maxLength,
          validation.message,
          validation.startIndex
        );
        if (!isLengthOk) return;
        break;
      }
      case EValidationType.REQUIRED: {
        this.checkColumnExists(
          jsonData,
          validation.column,
          validation.message,
          validation.startIndex
        );
        break;
      }
      default:
        return;
    }
  }

  checkColumnUniqueness(
    data: any[],
    column: string | number,
    message: string,
    startRowIndex = 0
  ): void {
    if (!this.columnExists(data, column)) {
      this.triggerValidationError(message, 'invalid');
      return;
    }

    const seen = new Set();
    const duplicates: { value: any; message: string }[] = [];

    for (let i = 0; i < data.length; i++) {
      if (i < startRowIndex) continue;
      const row = data[i];
      const value = this.getColumnValue(row, column);
      if (seen.has(value)) {
        duplicates.push({ value, message });
      } else {
        seen.add(value);
      }
    }

    if (duplicates.length > 0) {
      this.triggerValidationError(message, 'invalid', duplicates);
    }
  }

  checkColumnLength(
    data: any[],
    column: string | number,
    minLength: number,
    maxLength: number,
    message: string,
    startRowIndex = 0
  ): boolean {
    if (!this.columnExists(data, column)) {
      this.triggerValidationError(message, 'invalid');
      return false;
    }

    const invalidEntries: { value: any; message: string }[] = [];

    for (let i = 0; i < data.length; i++) {
      if (i < startRowIndex) continue;
      const row = data[i];
      const value = this.getColumnValue(row, column);

      if (typeof value === 'string' || typeof value === 'number') {
        const length = String(value).length;
        const tooShort = minLength != null && length < minLength;
        const tooLong = maxLength != null && length > maxLength;

        if (tooShort || tooLong) {
          invalidEntries.push({ value, message });
        }
      } else {
        invalidEntries.push({ value, message });
      }
    }

    if (invalidEntries.length > 0) {
      this.triggerValidationError(message, 'invalid', invalidEntries);
      return false;
    }

    return true;
  }

  checkColumnExists(
    data: any[],
    column: string | number,
    message: string,
    startIndex = 0
  ): void {
    if (!this.columnIsRequiredAndFilled(data, column, startIndex)) {
      this.triggerValidationError(
        message ?? this.invalidAttachmentMessage,
        'required'
      );
    }
  }

  private base64ToArrayBuffer(dataURL: string): ArrayBuffer {
    const base64 = dataURL.split(',')[1];
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private columnExists(data: any[], column: string | number): boolean {
    if (!data || data.length === 0) return false;

    if (typeof column === 'string') {
      return data.some(row =>
        Object.prototype.hasOwnProperty.call(row, column)
      );
    } else if (typeof column === 'number') {
      return data.some(row => {
        const values = Object.values(row);
        return column >= 0 && column < values.length;
      });
    }

    return false;
  }

  private getColumnValue(row: any, column: string | number): any {
    if (typeof column === 'number') {
      const values = Object.values(row);
      return values[column];
    } else {
      return row[column];
    }
  }

  private triggerValidationError(
    message: string,
    target: 'required' | 'invalid',
    issues?: any[]
  ): void {
    this.fileAttachmentNotValid = true;
    this.hasErrors = true;
    this._onChange(this.file);
    this._onTouch(this.file);

    if (target === 'required') {
      this.requiredColumnMessage = message;
      this.isValidationRequiredColumn = true;
    } else {
      this.validationsMessage = message;
      if (issues) {
        this.validationsIsssuesDetails = [
          ...(this.validationsIsssuesDetails || []),
          ...issues,
        ];
      }
      this.isInvalidAttachment = true;
    }

    this.cd.detectChanges();
  }

  private columnIsRequiredAndFilled(
    data: any[],
    column: string | number,
    startIndex = 0
  ): boolean {
    if (!data || data.length <= startIndex) return false;

    for (let i = startIndex; i < data.length; i++) {
      const row = data[i];

      if (typeof column === 'string') {
        if (
          !Object.prototype.hasOwnProperty.call(row, column) ||
          row[column] === null ||
          row[column] === undefined ||
          String(row[column]).trim() === ''
        ) {
          return false;
        }
      } else if (typeof column === 'number') {
        const values = Object.values(row);
        if (
          column >= values.length ||
          values[column] === null ||
          values[column] === undefined ||
          String(values[column]).trim() === ''
        ) {
          return false;
        }
      }
    }

    return true;
  }
}
